"use client";
"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Tabs = void 0;
const react_1 = __importDefault(require("react"));
const foundations_1 = require("../../foundations");
const Icon_1 = require("../../foundations/Icon/Icon");
const Text_1 = require("../../foundations/Typography/Text");
const useId_1 = require("../../hooks/useId");
const useWarning_1 = require("../../hooks/useWarning");
const Tabs = ({ items, defaultActive, alignment = "horizontal", baseline = true, size = "medium", onClickTab }) => {
    var _a;
    const colorClassName = `tab${(0, useId_1.useId)().replace(/^[A-Z0-9]gi/, "")}`;
    const { warn } = (0, useWarning_1.useWarning)();
    if (items.length === 0) {
        warn("Tabs requires at least one tab item in items prop");
    }
    const initialTab = items.find((item) => item.id === defaultActive)
        ? defaultActive
        : (_a = items[0]) === null || _a === void 0 ? void 0 : _a.id;
    const [activeTab, setActiveTab] = react_1.default.useState(initialTab);
    const onSetActiveTab = react_1.default.useCallback((tabId) => {
        setActiveTab(tabId);
        onClickTab(tabId);
    }, [onClickTab]);
    const containerStyles = react_1.default.useMemo(() => {
        if (alignment === "horizontal") {
            return {
                alignItems: "center",
                px: size === "medium" ? "spacing-3" : "spacing-4",
                paddingTop: "spacing-2",
                borderBottom: baseline ? "1px solid" : "unset",
                borderColor: baseline ? "stroke-neutral-tertiary" : "unset"
            };
        }
        else {
            return {
                alignItems: "flex-start",
                py: "spacing-3",
                paddingRight: "spacing-4",
                borderLeft: baseline ? "1px solid" : "unset",
                borderColor: baseline ? "stroke-neutral-tertiary" : "unset"
            };
        }
    }, [alignment, baseline, size]);
    const getColorStyles = react_1.default.useCallback((isActive, isDisabled) => {
        return Object.assign({ [`.${colorClassName}`]: {
                borderColor: isActive ? "tab-text-selected" : "transparent",
                color: isDisabled
                    ? "tab-text-disabled"
                    : isActive
                        ? "tab-text-selected"
                        : "tab-text-default",
                transition: "transition-color-medium"
            } }, (!isDisabled &&
            !isActive && {
            [`:hover .${colorClassName}`]: {
                borderColor: "tab-stroke-hover"
            },
            [`:active .${colorClassName}`]: {
                borderColor: "tab-stroke-click"
            }
        }));
    }, [colorClassName]);
    const getAlignmentStyles = react_1.default.useCallback((isActive) => {
        if (alignment === "horizontal") {
            return {
                px: "spacing-2",
                py: size === "medium" ? "spacing-4" : "spacing-5",
                marginBottom: "-1px",
                borderBottom: isActive ? "3px solid" : "3px solid transparent",
                transition: "transition-color-medium"
            };
        }
        else {
            return {
                px: size === "medium" ? "spacing-3" : "spacing-4",
                py: size === "medium" ? "spacing-2" : "spacing-3",
                marginLeft: "-1px",
                borderLeft: isActive ? "3px solid" : "3px solid transparent",
                transition: "transition-color-medium"
            };
        }
    }, [alignment, size]);
    const tabFocusStyles = react_1.default.useMemo(() => {
        return {
            ":focus-visible": {
                outline: "2px solid",
                outlineColor: "focus",
                borderRadius: "radius-1"
            }
        };
    }, []);
    return (react_1.default.createElement(foundations_1.Box, { role: "tablist", "aria-orientation": alignment, sx: {
            display: "flex",
            flexDirection: alignment === "horizontal" ? "row" : "column"
        } }, items.map((item) => {
        const isActive = item.id === activeTab;
        const isDisabled = item.disabled ? item.disabled : false;
        return (react_1.default.createElement(foundations_1.Target, Object.assign({}, item, { key: item.id, role: "tab", "aria-selected": isActive, disabled: isDisabled, onClick: () => onSetActiveTab(item.id), sx: Object.assign(Object.assign({}, getColorStyles(isActive, isDisabled)), tabFocusStyles) }),
            react_1.default.createElement(foundations_1.Box, { sx: containerStyles },
                react_1.default.createElement(foundations_1.Box, { className: colorClassName, sx: Object.assign({ display: "flex", alignItems: "center", gap: "spacing-2" }, getAlignmentStyles(isActive)) },
                    item.icon && (react_1.default.createElement(Icon_1.Icon, { color: "currentColor", size: size === "medium" ? "xs" : "sm", icon: item.icon })),
                    react_1.default.createElement(Text_1.Text, { variant: size === "medium" ? "title-1-medium" : "h5", sx: { color: "currentColor" } }, item.label)))));
    })));
};
exports.Tabs = Tabs;
//# sourceMappingURL=Tabs.js.map